import type { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useManualAddCompletion } from "@/components/RequestData/RequestsTab/useManualAddCompletion";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useSnackbar } from "@/contexts/SnackbarContext";
import { useLatestRef } from "@/hooks/useLatestRef";
import { useNurseTimeOff } from "@/hooks/useNurseTimeOff";
import { useAddSingleShiftByDateAndNurse } from "@/pages/Profile/v2/WeeklySchedule/XWeekScheduleGrid/hooks";
import { isInPTO } from "@/utils/dateUtils";
import type { Nurse, NurseInShift } from "@/utils/models";
import { getRespondedNurseIds } from "@/utils/utilFunctions";
import { useShiftPredictionWarnings } from "@/utils/warningDialogs/useShiftPredictionWarnings";
import { isAgencyNurse } from "@/utils/warningDialogs/utils";
import { buildEmployeeStatusWarning } from "@/utils/warningDialogs/WarningDialog";
import { isSectionWriteBlocked } from "../gapsGroupSheets";
import { type GapsAddContext, jobIdForAdd } from "./gapsAddContext";

/** The `source` the manual-add completion's own analytics record for an add made here. */
const COMPLETION_SOURCE = "gaps_add_staff_sheet";

/** `gaps_add_staff_cancelled`'s `reason` when the add stopped because its linked request left the list. */
const CANCELLED_LINKED_REQUEST_GONE = "linked_request_gone";

type Pending = { nurse: Nurse; properties: Record<string, string | number | boolean | null> };

/**
 * The add sheet's write (P3-7, P3-8): the desktop's warning gates, then `addShift`, then — for a hole that has a
 * posted request — the manual-add completion that decrements or closes that request.
 *
 * One add per opening at a time. The wrapped hooks' own loading flags end before the whole write does (`addShift`
 * still awaits a requests refresh after its own mutation settles), so this hook keeps its own in-flight state, from
 * the tap to the end of the write: a second tap in that window would add the person twice. Once the add is done the
 * sheet stays busy while it hands on or closes, so it cannot be pressed again during the exit.
 *
 * Gates: `useShiftPredictionWarnings.runChecks` — overtime and daily hours from the backend's prediction, agency, the
 * staffing target for the unit, On Leave / Suspended — with PTO computed from the nurse's own preferences (`isInPTO`),
 * as the desktop quick-add does. The classic mobile screen passed `hasPto: false`; that is deliberately not copied.
 * Each warning is a `WarningDialog`, which covers the bottom bar (P3-13). Confirming the last one adds; cancelling
 * any of them adds nothing.
 *
 * A LINKED add re-checks its request at the write, not only at the tap (M8): the checks await a prediction and a
 * warning can sit open for as long as the user reads it, and a request filled, deleted or merged away meanwhile would
 * otherwise still be written to — the person added, then `completeManualAdd` failing. `isLinkedRequestLost` is read
 * through a ref, as the screen shows it when the last warning is confirmed; a lost request adds nobody.
 *
 * Nothing is added under a section that cannot say which job it is (`isWriteBlocked`, an unlinked add only — a linked
 * add writes the request's own job), whatever the caller's button does; the step says why.
 *
 * The wrapped hooks keep their own analytics (`Staffing - add nurse`, `ManualStaffAddition_*`) and snackbars.
 */
export const useGapsAddStaff = ({
	context,
	isLinkedRequestLost,
	date,
	onAdded,
}: {
	context: GapsAddContext;
	/** The linked request is not in the live list now (see `GapsAddLink.isLost`); always `false` for an unlinked add. */
	isLinkedRequestLost: boolean;
	date: DateTime;
	onAdded: (created: NurseInShift) => void;
}) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { chosenDepartment } = useHospitalData();
	const { addShift } = useAddSingleShiftByDateAndNurse({ selectedDate: date });
	const { completeManualAdd } = useManualAddCompletion();
	const timeOffOf = useNurseTimeOff();
	const [isChecking, setIsChecking] = useState<boolean>(false);
	const [isWriting, setIsWriting] = useState<boolean>(false);
	const inFlightRef = useRef<boolean>(false);
	const isLinkedRequestLostRef = useLatestRef(isLinkedRequestLost);
	const isWriteBlocked = context.linkedRequest === null && isSectionWriteBlocked(context.group);
	// The person waiting on the warning cascade. A ref, not state: with no warnings the cascade confirms in the same
	// tick as the tap, before a state write would have re-rendered — the reason every add dialog holds it this way.
	const pendingRef = useRef<Pending | null>(null);

	/** The add ended without adding anyone: the footer can be pressed again. */
	const release = useCallback((): void => {
		inFlightRef.current = false;
		setIsWriting(false);
	}, []);

	const performAdd = useCallback(async (): Promise<void> => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		if (pending === null) return;
		if (isLinkedRequestLostRef.current) {
			// The request left the list while the checks ran or a warning was up. Writing now would put the person on the
			// shift and then complete nothing; the step already says the request is gone, and its Add stays off.
			track("gaps_add_staff_cancelled", { ...pending.properties, reason: CANCELLED_LINKED_REQUEST_GONE });
			release();
			return;
		}
		setIsWriting(true);
		const { times, locationName, linkedRequest } = context;
		const created = await addShift(
			pending.nurse,
			times.startTime,
			times.endTime,
			locationName === null ? undefined : locationName,
			undefined,
			undefined,
			jobIdForAdd(context),
		);
		if (created === false) {
			// `addShift` has already shown why (the backend's own reason when it gave one).
			trackError("gaps_add_staff_error", pending.properties);
			release();
			return;
		}
		if (linkedRequest !== null) {
			try {
				await completeManualAdd({ request: linkedRequest, approvedNursesCount: getRespondedNurseIds(linkedRequest).length, source: COMPLETION_SOURCE });
			} catch (error) {
				// The person IS on the shift; only the request's count is behind. Say exactly that, rather than "failed".
				trackError("gaps_add_staff_request_update_error", { ...pending.properties, error_message: error instanceof Error ? error.message : String(error) });
				showSnackbar(t("gaps.addSheet.requestUpdateFailed"), "warning");
			}
		}
		track("gaps_add_staff_success", { ...pending.properties, shift_id: created.id });
		onAdded(created);
	}, [addShift, completeManualAdd, context, isLinkedRequestLostRef, onAdded, release, showSnackbar, t, track, trackError]);

	// The cascade calls back into whichever `performAdd` was current when it was CREATED; routing through a ref makes
	// it the latest one (the classic screen's fix for the same stale-callback trap).
	const performAddRef = useRef(performAdd);
	useEffect(() => {
		performAddRef.current = performAdd;
	}, [performAdd]);

	const warnings = useShiftPredictionWarnings(() => void performAddRef.current());

	const add = useCallback(
		async (nurse: Nurse): Promise<void> => {
			// The step's Add is off in both cases; refused here too, as the post hook refuses its own (`canPost`).
			if (inFlightRef.current || isWriteBlocked || isLinkedRequestLostRef.current) return;
			inFlightRef.current = true;
			const { times, locationName, linkedRequest, group } = context;
			const hasPto = timeOffOf(nurse.id).some((preference) => isInPTO(preference, times.startTime, chosenDepartment.dayStart, times.endTime));
			const properties = {
				department_id: chosenDepartment.department_id,
				group: group.key,
				nurse_id: nurse.id,
				location: locationName,
				linked_request_id: linkedRequest === null ? null : linkedRequest.id,
				has_pto: hasPto,
			};
			pendingRef.current = { nurse, properties };
			track("gaps_add_staff_start", properties);
			setIsChecking(true);
			await warnings.runChecks({
				nurseId: nurse.id,
				startTime: times.startTime,
				endTime: times.endTime,
				isGlobalSalary: nurse.isGlobalSalary,
				isAgency: isAgencyNurse(nurse.employmentType),
				hasPto,
				// The staffing target is a unit's; an add with no unit yet has none to check.
				locationName: locationName === null ? undefined : locationName,
				employeeStatusWarning: buildEmployeeStatusWarning(nurse, chosenDepartment.department_id, times.startTime),
			});
			setIsChecking(false);
		},
		[chosenDepartment.dayStart, chosenDepartment.department_id, context, isLinkedRequestLostRef, isWriteBlocked, timeOffOf, track, warnings],
	);

	const cancelWarnings = useCallback((): void => {
		const pending = pendingRef.current;
		pendingRef.current = null;
		if (pending !== null) track("gaps_add_staff_cancelled", pending.properties);
		warnings.handleCancelWarnings();
		release();
	}, [release, track, warnings]);

	/**
	 * `isBusy`: while the checks run and while the write runs (and through the exit after a success). `isWriting`: the
	 * write alone — the only span in which this add can be what closed its linked request, so the step's "gone" waits on
	 * it, never on the checks or an open warning.
	 */
	return { add, cancelWarnings, warnings, isBusy: isChecking || isWriting, isWriting, isWriteBlocked };
};
